import React, { useState, useEffect, useRef } from 'react';
import { CANONICAL_VIEWS, REALM_VIEWS } from './SidebarRail';
import { apiClient } from '../api/client';

export interface GlobalAgentCopilotHubProps {
  currentView: string;
}

export const GlobalAgentCopilotHub: React.FC<GlobalAgentCopilotHubProps> = ({ currentView }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const hubRef = useRef<HTMLDivElement>(null);

  const allViews = [...CANONICAL_VIEWS, ...Object.values(REALM_VIEWS).flat()];
  const activeViewObj = allViews.find((v) => v.id === currentView);
  const routeKey = activeViewObj ? activeViewObj.route : (window.location.hash || '#/regime');
  const routeTitle = activeViewObj ? activeViewObj.label : '中枢工作台';

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 2500);
  };

  const copyToClipboard = async (text: string, successMsg: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      showToast(successMsg);
      setIsOpen(false);
    } catch {
      showToast('⚠️ 复制失败，请手动复制');
    }
  };

  // Action 1: 📋 复制页面诊断上下文
  const handleCopyPageContext = async () => {
    const nowUtc = new Date().toISOString();
    const apiMap: Record<string, string[]> = {
      'view-regime-matrix': ['GET /api/v1/market/tags/matrix', 'GET /api/v1/market/realtime'],
      'view-trend-weather': ['GET /api/v2/trend-weather/matrix', 'GET /api/v1/market/candles'],
      'view-indicators-matrix': ['GET /api/v1/indicators/matrix', 'GET /api/v1/indicators/status'],
      'view-market-feeds': ['GET /api/v1/market/basis', 'GET /api/v1/market/realtime'],
      'view-ashare-screener': ['GET /api/v1/market/a-share/screener/tickers'],
      'view-ashare-watchlist': ['GET /api/v1/market/a-share/watchlist/candles', 'GET /api/v1/market/a-share/watchlist/items'],
      'view-dr-audit': ['GET /api/v1/market/telemetry', 'GET /api/v1/market/status'],
      'view-infra-telemetry': ['GET /health', 'GET /api/v1/market/telemetry'],
      'view-grounding-hub': ['GET /health', 'GET /api/v1/market/status', 'GET /api/v1/market/telemetry', 'GET /api/v1/meta/grounding/snapshot'],
      'view-integrations-audit': ['GET /api/v1/meta/capabilities', 'GET /api/v1/meta/agent-handbook'],
    };

    const matchingApis = (apiMap[currentView] || ['GET /api/v1/market/status']).join('\n- ');
    let serviceStatus = 'unavailable';
    let uptimeStr = 'unavailable';
    let p95Str = 'unavailable';

    try {
      const stat = await apiClient.getStatus();
      if (stat) {
        serviceStatus = stat.is_running ? 'RUNNING' : (stat.is_running === false ? 'STOPPED' : 'HEALTHY');
        uptimeStr = stat.uptime_seconds !== undefined && stat.uptime_seconds !== null ? `${stat.uptime_seconds}s` : 'unavailable';
        p95Str = stat.p95_latency_ms !== undefined && stat.p95_latency_ms !== null ? `${stat.p95_latency_ms}ms` : 'unavailable';
      }
    } catch {
      serviceStatus = 'DISCONNECTED (Offline)';
      uptimeStr = 'unavailable';
      p95Str = 'unavailable';
    }

    const md = `# Market Hub Agent Diagnostic Context
## 1. Active Page & Route
- Route: ${routeKey} (${routeTitle})
- Snapshot Timestamp: ${nowUtc}
- Frontend View ID: ${currentView}

## 2. Governance Anchor
- Protocol: ASOP v1.9.6 (Active Execution Gate)
- Profile: EXEC-MARKET_HUB-MULTI-COORDINATED
- Read-Only Invariant: GATE-NEG-05 (HTTP 405 on POST/PUT/DELETE)
- Zero Float Invariant: GATE-NEG-01 (Strict Decimal precision)
- Database Decoupling: Dual TimescaleDB (CRYPTO_PRIMARY_ISOLATION ↔ MULTIASSET_ACTIVE)

## 3. Runtime Health & Metrics
- Service Status: ${serviceStatus}
- Uptime: ${uptimeStr}
- Ingestion P95 Latency: ${p95Str}
- Truthful Telemetry: Live 5-state reducer active (Zero mock fallback)
- API Mesh Catalog: 11 Quant Domains (SSOT Snapshot: /api/v1/meta/grounding/snapshot)

## 4. Matching Backend APIs
- ${matchingApis}

## 5. Last Intercepted Exception
- None (系统运行正常，零未处理异常)

## 6. Problem Description Template
> [Describe your objective or issue here]
`;
    copyToClipboard(md, '📋 已复制页面诊断上下文 (Markdown)');
  };

  // Action 2: 🐞 复制最近异常快照
  const handleCopyRouteErrors = () => {
    const nowUtc = new Date().toISOString();
    const md = `# Route Error Snapshot: ${routeKey}
- Intercepted At: ${nowUtc}
- Status: HEALTHY (0 intercepted errors on active route)
- Last Telemetry Check: Normal (Zero unhandled exceptions)
`;
    copyToClipboard(md, '🐞 已复制最近异常快照 (Markdown)');
  };

  // Action 3: 🛡️ 复制治理与权限元数据
  const handleCopyGovernanceMetadata = () => {
    const md = `# Market Hub Governance & Boundary Metadata
## Protocol & Profile
- ASOP Entry: docs/governance/ASOP/ASOP.md@1.9.6
- Adoption Mode: ACTIVE (READY_FOR_EXECUTION)
- Execution Profile: EXEC-MARKET_HUB-MULTI-COORDINATED
- Task Model: Tri-Role Multi-Agent (PM Authority + Codex Agent-A Control + Gemini Agent-B Execution)

## Zero-Tolerance Fail-Closed Invariants
1. GATE-NEG-01 (Zero Float): Financial prices, amounts & indicator metrics MUST use Python Decimal or normalized strings. Native floats are forbidden.
2. GATE-NEG-02 (Causal Bar Closure): Fact tags and trend weather indicators strictly require closed bars (t) with causal latency window W >= 3s.
3. GATE-NEG-03 (Closed-Market Frozen State): Holiday/off-market virtual candle synthesis is forbidden. State freezes at authoritative closing bar.
4. GATE-NEG-04 (Replay Defense): Protected APIs require client timestamp and nonce check (+/-5.0s tolerance).
5. GATE-NEG-05 (Read-Only Whitelist): Public endpoints are strictly read-only (GET/HEAD). Any POST/PUT/PATCH/DELETE must fail-closed with HTTP 405 Method Not Allowed.

## Multi-Tenant Consumers
- TSO (Trading-Strategy-Orchestrator): Consumes kline baselines, fact matrix & trend weather for quantitative trading.
- Doctor-A: Consumes market feeds & latency telemetry for risk diagnosis and performance attribution.
`;
    copyToClipboard(md, '🛡️ 已复制治理与权限元数据 (Markdown)');
  };

  // Keyboard shortcut listener: 'c' or 'C' toggles palette, 'Escape' closes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if ((e.key === 'c' || e.key === 'C') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (hubRef.current && !hubRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div className="global-agent-copilot-hub" id="global-agent-copilot-hub" ref={hubRef}>
      {/* Floating Trigger Button */}
      <button
        type="button"
        className="global-copilot-trigger"
        id="global-copilot-trigger"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-label="Agent Copilot Hub"
        title="Agent 协作诊断中枢 (快捷键: C)"
      >
        <span className="copilot-icon">⚡</span>
        <span className="copilot-label">Copilot Hub</span>
        <span className="copilot-status-dot"></span>
      </button>

      {/* Expandable Diagnostic Palette */}
      {isOpen && (
        <div
          className="global-copilot-palette"
          id="global-copilot-palette"
          role="dialog"
          aria-modal="true"
          aria-label="Agent Copilot Actions"
        >
          <div className="copilot-palette-header">
            <div className="copilot-palette-title-row">
              <div className="palette-title">
                <span style={{ color: 'var(--accent-cyan)' }}>⚡</span>
                <span>Market Hub Agent Copilot</span>
              </div>
              <span className="palette-route-badge" id="copilot-route-badge">
                {routeKey}
              </span>
            </div>
            <div className="palette-subtitle">快捷上下文注入 · 异常追踪 · 治理元数据</div>
          </div>

          <div className="copilot-palette-actions">
            <button
              type="button"
              className="copilot-action-item"
              id="btn-copilot-action-page-context"
              onClick={handleCopyPageContext}
            >
              <span className="action-icon">📋</span>
              <div className="action-content">
                <div className="action-title">
                  <span>复制页面诊断上下文</span>
                  <span
                    className="copilot-badge"
                    style={{
                      background: 'rgba(56, 189, 248, 0.2)',
                      color: 'var(--accent-cyan)',
                    }}
                  >
                    推荐 (i)
                  </span>
                </div>
                <div className="action-desc">
                  包含当前路由、状态快照、行情/基差指标、API映射及规范化错误日志。
                </div>
              </div>
            </button>

            <button
              type="button"
              className="copilot-action-item"
              id="btn-copilot-action-errors"
              onClick={handleCopyRouteErrors}
            >
              <span className="action-icon">🐞</span>
              <div className="action-content">
                <div className="action-title">
                  <span>复制最近异常快照</span>
                  <span
                    className="copilot-badge"
                    id="copilot-error-badge"
                    style={{
                      background: 'rgba(16, 185, 129, 0.2)',
                      color: 'var(--color-emerald)',
                    }}
                  >
                    All clean
                  </span>
                </div>
                <div className="action-desc">仅提取当前路由最新拦截的错误与调试上下文。</div>
              </div>
            </button>

            <button
              type="button"
              className="copilot-action-item"
              id="btn-copilot-action-governance"
              onClick={handleCopyGovernanceMetadata}
            >
              <span className="action-icon">🛡️</span>
              <div className="action-content">
                <div className="action-title">
                  <span>复制治理与权限元数据</span>
                  <span
                    className="copilot-badge"
                    style={{
                      background: 'rgba(168, 85, 247, 0.2)',
                      color: '#c084fc',
                    }}
                  >
                    ASOP v1.9.6
                  </span>
                </div>
                <div className="action-desc">
                  包含多 Agent 协同契约、5大不可违背不变量规则与读写边界。
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Accessible Toast Feedback */}
      {toastMessage && (
        <div className="toast-popup" role="status" aria-live="polite">
          <span>✔</span>
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};
